import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getCurrentUser } from '../services/authService';
import { usePresenceContext } from '../contexts/PresenceContext';
import '../styles/Friends.css';
import { useNavigate } from 'react-router-dom';

function Friends() {
  const [friends, setFriends] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [friendRequests, setFriendRequests] = useState([]);
  const navigate = useNavigate();
  const { getPresence, formatLastSeen, requestBulkPresence } = usePresenceContext();

  const API_URL = process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'production'
      ? 'https://chitchat-3l35.onrender.com'
      : 'http://localhost:5000/api'
    );

  console.log('API_URL configured as:', API_URL);
  console.log('Environment:', process.env.NODE_ENV);

  const getInitial = (name) => (name ? name.charAt(0).toUpperCase() : '?');

  const fetchFriends = useCallback(async () => {
    try {
      setLoading(true);
      const currentUser = getCurrentUser();
      console.log('Current user in fetchFriends:', currentUser);
      if (!currentUser) {
        console.error('Authentication issue: No user found');
        setError('You need to be logged in. Redirecting to login...');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }
      if (!currentUser.token) {
        console.error('Authentication issue: No token found', currentUser);
        const rawUser = localStorage.getItem('user');
        console.log('Raw localStorage user value:', rawUser);
        setError('Invalid authentication. Redirecting to login...');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }
      console.log('Fetching friends from:', `${API_URL}/friends`);
      const response = await axios.get(`${API_URL}/friends`, {
        headers: { Authorization: `Bearer ${currentUser.token}` }
      });
      console.log('Friends data:', response.data);
      setFriends(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching friends:', err);
      setLoading(false);
      if (err.response?.status === 401) {
        console.error('Unauthorized access. Token might be invalid or expired.');
        localStorage.removeItem('user');
        setError('Your session has expired. Please login again.');
        setTimeout(() => navigate('/login'), 2000);
      } else if (err.response?.status === 404) {
        setError('Friend endpoint not found. Please check your API configuration.');
      } else {
        setError(err.response?.data?.error || err.message || 'An error occurred while fetching friends');
      }
    }
  }, [navigate, API_URL]);

  const fetchFriendRequests = useCallback(async () => {
    try {
      const currentUser = getCurrentUser();
      if (!currentUser || !currentUser.token) return;
      const response = await axios.get(`${API_URL}/friends/requests`, {
        headers: { Authorization: `Bearer ${currentUser.token}` }
      });
      setFriendRequests(response.data);
    } catch (err) {
      console.error('Error fetching friend requests:', err);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchFriends();
    fetchFriendRequests();
  }, [fetchFriends, fetchFriendRequests]);

  // Request bulk presence when friends are loaded
  useEffect(() => {
    if (friends.length > 0) {
      requestBulkPresence(friends.map(f => f.id));
    }
  }, [friends, requestBulkPresence]);

  // Sort friends: online first, then by username
  const sortedFriends = useMemo(() => {
    return [...friends].sort((a, b) => {
      const aOnline = getPresence(a.id).status === 'online';
      const bOnline = getPresence(b.id).status === 'online';
      if (aOnline && !bOnline) return -1;
      if (!aOnline && bOnline) return 1;
      return (a.username || '').localeCompare(b.username || '');
    });
  }, [friends, getPresence]);

  const searchUsers = async () => {
    if (!searchTerm) return;
    try {
      setLoading(true);
      const currentUser = getCurrentUser();
      if (!currentUser || !currentUser.token) { setError('You need to be logged in'); setLoading(false); return; }
      const response = await axios.get(`${API_URL}/users/search?q=${searchTerm}`, {
        headers: { Authorization: `Bearer ${currentUser.token}` }
      });
      const filteredResults = response.data.filter(user =>
        !friends.some(friend => friend.id === user.id)
      );
      setSearchResults(filteredResults);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      if (err.response?.status === 404) {
        setError('User search endpoint not found. Please check your API configuration.');
      } else {
        setError(err.response?.data?.error || 'An error occurred while searching users');
      }
    }
  };

  const sendFriendRequest = async (userId) => {
    try {
      setLoading(true);
      const currentUser = getCurrentUser();
      await axios.post(`${API_URL}/friends/${userId}`, {}, {
        headers: { Authorization: `Bearer ${currentUser.token}` }
      });
      setSearchResults(searchResults.map(user =>
        user.id === userId ? { ...user, requestSent: true } : user
      ));
      setLoading(false);
      setError('Friend request sent successfully!');
      setTimeout(() => setError(''), 3000);
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.error || 'Failed to send friend request');
    }
  };

  const acceptFriendRequest = async (requestId) => {
    try {
      setLoading(true);
      const currentUser = getCurrentUser();
      await axios.put(`${API_URL}/friends/requests/${requestId}/accept`, {}, {
        headers: { Authorization: `Bearer ${currentUser.token}` }
      });
      await fetchFriendRequests();
      await fetchFriends();
      setLoading(false);
      setError('Friend request accepted successfully!');
      setTimeout(() => setError(''), 3000);
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.error || 'Failed to accept friend request');
    }
  };

  const rejectFriendRequest = async (requestId) => {
    try {
      setLoading(true);
      const currentUser = getCurrentUser();
      await axios.put(`${API_URL}/friends/requests/${requestId}/reject`, {}, {
        headers: { Authorization: `Bearer ${currentUser.token}` }
      });
      setFriendRequests(friendRequests.filter(request => request.id !== requestId));
      setLoading(false);
      setError('Friend request rejected');
      setTimeout(() => setError(''), 3000);
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.error || 'Failed to reject friend request');
    }
  };

  const removeFriend = async (friendId) => {
    try {
      setLoading(true);
      const currentUser = getCurrentUser();
      await axios.delete(`${API_URL}/friends/${friendId}`, {
        headers: { Authorization: `Bearer ${currentUser.token}` }
      });
      setFriends(friends.filter(friend => friend.id !== friendId));
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.error || 'Failed to remove friend');
    }
  };

  const startChat = (friendId) => navigate(`/chat/${friendId}`);

  return (
    <div className="friends-container">
      {/* Page Header */}
      <div className="friends-page-header">
        <h1>💬 Friends</h1>
        <p>Connect, chat, and play together with the people you love</p>
      </div>

      {/* Status Messages */}
      {error && (
        <div className={error.includes('successfully') || error.includes('rejected') ? 'success-message' : 'error-message'}>
          {error.includes('successfully') || error.includes('rejected')
            ? `✅ ${error}`
            : <>⚠️ {error} {!error.includes('successfully') && (
              <button onClick={() => window.location.reload()}>Retry</button>
            )}</>
          }
        </div>
      )}

      {/* Friend Requests */}
      {friendRequests.length > 0 && (
        <div className="section-card requests-section">
          <div className="section-title">
            🔔 Friend Requests
            <span className="section-title-badge">{friendRequests.length}</span>
          </div>
          <div className="friend-requests">
            {friendRequests.map(request => (
              <div className="user-card" key={request.id}>
                <div className="card-avatar">{getInitial(request.username)}</div>
                <div className="user-info">
                  <h3>{request.username}</h3>
                  <p>{request.email}</p>
                </div>
                <div className="request-actions">
                  <button
                    onClick={() => acceptFriendRequest(request.id)}
                    disabled={loading}
                    className="accept-btn"
                  >
                    ✓ Accept
                  </button>
                  <button
                    onClick={() => rejectFriendRequest(request.id)}
                    disabled={loading}
                    className="reject-btn"
                  >
                    ✕ Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search Section */}
      <div className="section-card search-section">
        <div className="section-title">🔍 Find Friends</div>
        <div className="search-bar">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by username or email..."
            onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
          />
          <button onClick={searchUsers} disabled={loading || !searchTerm}>
            {loading ? '...' : '🔍 Search'}
          </button>
        </div>

        <div className="search-results">
          {loading ? (
            <div className="loading">Searching...</div>
          ) : searchResults.length > 0 ? (
            searchResults.map(user => (
              <div className="user-card" key={user.id}>
                <div className="card-avatar cyan">{getInitial(user.username)}</div>
                <div className="user-info">
                  <h3>{user.username}</h3>
                  <p>{user.email}</p>
                </div>
                <button
                  onClick={() => sendFriendRequest(user.id)}
                  disabled={user.requestSent || loading}
                  className={user.requestSent ? 'sent' : 'add-friend-btn'}
                >
                  {user.requestSent ? '✓ Sent' : '+ Add'}
                </button>
              </div>
            ))
          ) : searchTerm && !loading ? (
            <div className="no-results">No users found matching "{searchTerm}"</div>
          ) : null}
        </div>
      </div>

      {/* Friends List */}
      <div className="section-card">
        <div className="section-title">
          👥 My Friends
          {friends.length > 0 && <span className="section-title-badge">{friends.length}</span>}
        </div>
        {loading ? (
          <div className="loading">Loading friends...</div>
        ) : friends.length > 0 ? (
          sortedFriends.map(friend => {
            const presence = getPresence(friend.id);
            const isOnline = presence.status === 'online';
            return (
            <div className="friend-card" key={friend.id}>
              <div className="card-avatar">
                {getInitial(friend.username)}
                {isOnline && <div className="online-dot" />}
              </div>
              <div className="friend-info">
                <h3>{friend.username}</h3>
                <p className={`friend-status ${isOnline ? 'status-online' : 'status-offline'}`}>
                  {isOnline ? '● Online' : formatLastSeen(presence.lastSeen)}
                </p>
              </div>
              <div className="friend-actions">
                <button onClick={() => startChat(friend.id)} className="chat-btn">
                  💬 Chat
                </button>
                <button onClick={() => removeFriend(friend.id)} className="remove-btn">
                  Remove
                </button>
              </div>
            </div>
            );
          })
        ) : (
          <div className="empty-state">
            <span className="empty-state-icon">💌</span>
            <p>No friends yet! Use the search above to find and connect with people you know.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Friends;